<template>
  <div v-if="showModal" class="modal manage-address-modal">
    <div class="modal-content" @keydown.stop @keyup.stop @keypress.stop>
      <div class="modal-header">
        <div class="modal-title">
          {{ id ? 'Update Address' : 'Create Address' }}
        </div>
        <button class="close-btn" @click="closeModal">
          <i class="fas fa-close"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-row">
          <div class="form-group" style="flex: 3">
            <label for="displayName">Display Name</label>
            <input v-model="address.displayName" type="text" id="displayName" />
          </div>
          <div class="form-group" style="flex: 3">
            <label for="businessName">Business Name</label>
            <input
              v-model="address.businessName"
              type="text"
              id="businessName"
            />
          </div>
          <div class="form-group" style="flex: 3">
            <label for="accountNumber">Account Number</label>
            <input
              v-model="address.accountNumber"
              type="text"
              id="accountNumber"
            />
          </div>
          <div class="form-group" style="flex: 1">
            <label for="is1099">1099</label>
            <label class="switch">
              <input type="checkbox" v-model="address.is1099" id="is1099" />
              <span class="slider round"></span>
            </label>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="firstName">First Name</label>
            <input v-model="address.firstName" type="text" id="firstName" />
          </div>
          <div class="form-group">
            <label for="middleName">Middle Name</label>
            <input v-model="address.middleName" type="text" id="middleName" />
          </div>
          <div class="form-group">
            <label for="lastName">Last Name</label>
            <input v-model="address.lastName" type="text" id="lastName" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="addressLine1">Address Line 1</label>
            <input
              v-model="address.addressLine1"
              type="text"
              id="addressLine1"
            />
          </div>
          <div class="form-group">
            <label for="addressLine2">Address Line 2</label>
            <input
              v-model="address.addressLine2"
              type="text"
              id="addressLine2"
            />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group" style="flex: 1">
            <label for="streetNumber">Street # </label>
            <input
              v-model="address.streetNumber"
              type="text"
              id="streetNumber"
            />
          </div>
          <div class="form-group" style="flex: 2">
            <label for="streetName">Street Name</label>
            <input v-model="address.streetName" type="text" id="streetName" />
          </div>
          <div class="form-group" style="flex: 1">
            <label for="streetDir">Dir.</label>
            <input v-model="address.streetDir" type="text" id="streetDir" />
          </div>
          <div class="form-group" style="flex: 1">
            <label for="unitNumber">Unit</label>
            <input v-model="address.unitNumber" type="text" id="unitNumber" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="city">City</label>
            <input v-model="address.city" type="text" id="city" />
          </div>
          <div class="form-group">
            <label for="state">State</label>
            <input
              v-model="address.state"
              type="text"
              id="state"
              maxlength="2"
              placeholder="MI"
              title="Two-letter state code (e.g. MI)"
              @input="address.state = (address.state || '').toUpperCase().slice(0, 2)"
            />
          </div>
          <div class="form-group">
            <label for="zipCode">Zip Code</label>
            <input v-model="address.zipCode" type="text" id="zipCode" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="phone">Phone</label>
            <input v-model="address.phone" type="text" id="phone" />
          </div>
          <div class="form-group">
            <label for="email">Email</label>
            <input v-model="address.email" type="email" id="email" />
          </div>
          <div class="form-group">
            <label for="website">Website</label>
            <input v-model="address.website" type="text" id="website" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="payeeType">Payee Type</label>
            <select v-model="address.payeeType" id="payeeType">
              <option value="1">Vendor</option>
              <option value="2">Tenant</option>
              <option value="3">Customer</option>
              <option value="4">Other</option>
            </select>
          </div>
          <div class="form-group">
            <label for="relatedClass">Related Class</label>
            <input
              v-model="address.relatedClass"
              type="text"
              id="relatedClass"
            />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="memo">Tax ID</label>
            <input v-model="address.taxId" id="taxId" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="memo">Memo</label>
            <textarea v-model="address.memo" id="memo"></textarea>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button @click="saveAddress">{{ id ? 'Update' : 'Save' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import axios from '@/axiosConfig';
import eventBus from './eventBus';

const props = defineProps({
  id: [String, Number, null],
  name: String,
  showModal: Boolean,
});

const address = ref({
  displayName: '',
  firstName: '',
  middleName: '',
  lastName: '',
  businessName: '',
  accountNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  email: '',
  website: '',
  phone: '',
  taxId: '',
  payeeType: null,
  relatedClass: '',
  memo: '',
  streetNumber: '',
  streetName: '',
  unitNumber: '',
  streetDir: '',
  is1099: 0,
});

const fetchAddressData = async () => {
  try {
    // Reset form first to clear any stale data, but preserve the displayName
    const currentDisplayName = props.name;
    resetForm();
    address.value.displayName = currentDisplayName;

    const response = await axios.get(
      `/api/get-address?name=${encodeURIComponent(props.name)}`,
    );
    if (
      response.status === 200 &&
      response.data &&
      Object.keys(response.data).length > 0
    ) {
      Object.assign(address.value, response.data);
    }
    // If empty response, the form stays reset with just displayName set
  } catch (error) {
    console.error('Error fetching address data:', error);
  }
};

watch([() => props.name, () => props.showModal], async ([newName, isVisible]) => {
  address.value.displayName = newName;
  if (isVisible) {
    await fetchAddressData();
    await nextTick();
    const el = document.getElementById('displayName');
    if (el && typeof el.focus === 'function') {
      el.focus();
      el.select?.();
    }
  }
});

watch(
  () => props.id,
  (newId) => {
    if (newId) {
      fetchAddressData();
    } else {
      resetForm();
    }
  },
);

const closeModal = () => {
  eventBus.emit('closeAddressModal');
};

const saveAddress = async () => {
  try {
    // DB column is VARCHAR(2) — keep only a 2-letter code on the client
    if (address.value.state) {
      address.value.state = String(address.value.state)
        .trim()
        .toUpperCase()
        .slice(0, 2);
    }
    const response = await axios.post('/api/add-address', {
      name: address.value.displayName,
      address: address.value,
    });
    if (response.status === 200 || response.status === 201) {
      closeModal();
    } else {
      console.error('Failed to save address');
      alert('Failed to save address');
    }
  } catch (error) {
    console.error('Error saving address:', error);
    const msg =
      error?.response?.data?.error ||
      error?.message ||
      'Failed to save address';
    alert(msg);
  }
};

const resetForm = () => {
  address.value = {
    displayName: '',
    firstName: '',
    middleName: '',
    lastName: '',
    businessName: '',
    accountNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    email: '',
    website: '',
    phone: '',
    taxId: '',
    payeeType: null,
    relatedClass: '',
    memo: '',
    streetNumber: '',
    streetName: '',
    unitNumber: '',
    streetDir: '',
    is1099: 0,
  };
};
</script>

<style scoped>
.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: none;
  /* Above Ledger Entry modal so payee edit from check preview is usable */
  z-index: 20000;
  pointer-events: none; /* This will make the modal not catch clicks */
}

.modal-content {
  pointer-events: auto; /* This will make the modal content catch clicks */
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 500px;
  max-width: 100%;
  border: 1pt solid #999;
  box-shadow: 9px 8px 7px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #333;
  color: #eee;
  padding: 10px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  margin: -20px -20px 20px -20px;
}

.close-btn {
  background: none;
  border: 1px solid #676666;
  font-size: 0.9rem;
  cursor: pointer;
  color: #eee;
  position: relative;
  top: -3px;
  right: -2px;
}

.modal-body {
  max-height: 400px;
  overflow-y: auto;
}

.form-row {
  display: flex;
  justify-content: space-between;
}

.form-group {
  margin-bottom: 15px;
  flex: 1;
  padding: 0 10px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-size: 0.7em;
  font-weight: bold;
  color: #444;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 6px;
  border-radius: 4px;
  border: 1px solid #dadada;
  box-sizing: border-box;
}

.form-group input {
  font-size: 0.75em !important;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #ddd;
  padding-top: 10px;
  margin-top: 20px;
}

.modal-footer button {
  padding: 10px 20px;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-footer button:hover {
  background-color: #0056b3;
}

/* create styles to make this an on/off switch 
  */

.switch {
  position: relative;
  display: inline-block;
  width: 30px;
  height: 17px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 17px;
}

.slider:before {
  position: absolute;
  content: '';
  height: 13px;
  width: 13px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #2196f3;
}

input:checked + .slider:before {
  transform: translateX(13px);
}
</style>
